import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import {
	AddJobApplicationPayload,
	AddJobApplicationResponse,
	AddStudentPayload,
	AddStudentResponse,
	AvailableYearsResponse,
	BulkAddStudentsResponse,
	CollegeActionResponse,
	CollegeProfileResponse,
	CollegeRegistrationResponse,
	PaginatedCollegeListResponse,
	PaginatedStudentApplicationsResponse,
	PaginatedStudentListResponse,
	SendCollegeOtpPayload,
	SendCollegeOtpResponse,
	StudentDetailResponse,
	StudentListParams,
	VerifyCollegeOtpPayload,
	VerifyCollegeOtpResponse,
} from '../models/college-portal.models';

@Injectable({
	providedIn: 'root'
})
export class CollegePortalService {
	apiUrl = environment.collegePortalApiUrl;

	constructor(private http: HttpClient) {}

	// ─────────────────────────────────────────────
	// Registration & own profile (college admin)
	// ─────────────────────────────────────────────

	registerCollege(formData: FormData): Observable<CollegeRegistrationResponse> {
		return this.http.post<CollegeRegistrationResponse>(`${this.apiUrl}/register`, formData);
	}

	sendRegistrationOtp(payload: SendCollegeOtpPayload): Observable<SendCollegeOtpResponse> {
		return this.http.post<SendCollegeOtpResponse>(`${this.apiUrl}/register/send-otp`, payload);
	}

	verifyRegistrationOtp(payload: VerifyCollegeOtpPayload): Observable<VerifyCollegeOtpResponse> {
		return this.http.post<VerifyCollegeOtpResponse>(`${this.apiUrl}/register/verify-otp`, payload);
	}

	getCollegeProfile(): Observable<CollegeProfileResponse> {
		return this.http.get<CollegeProfileResponse>(`${this.apiUrl}/me/profile`);
	}

	updateCollegeProfile(formData: FormData): Observable<CollegeProfileResponse> {
		return this.http.put<CollegeProfileResponse>(`${this.apiUrl}/me/profile`, formData);
	}

	// ─────────────────────────────────────────────
	// College admin: students
	// ─────────────────────────────────────────────

	addStudent(payload: AddStudentPayload): Observable<AddStudentResponse> {
		return this.http.post<AddStudentResponse>(`${this.apiUrl}/me/students`, payload);
	}

	addStudentsBulk(file: File, sendInvite: boolean): Observable<BulkAddStudentsResponse> {
		const formData = new FormData();
		formData.append('file', file);
		formData.append('send_invite', String(sendInvite));
		return this.http.post<BulkAddStudentsResponse>(`${this.apiUrl}/me/students/bulk`, formData);
	}

	getStudents(params: StudentListParams): Observable<PaginatedStudentListResponse> {
		let httpParams = new HttpParams()
			.set('page', params.page.toString())
			.set('limit', params.limit.toString());

		if (params.search) httpParams = httpParams.set('search', params.search);
		if (params.course) httpParams = httpParams.set('course', params.course);
		if (params.year_of_passing) httpParams = httpParams.set('year_of_passing', params.year_of_passing.toString());

		return this.http.get<PaginatedStudentListResponse>(`${this.apiUrl}/me/students`, { params: httpParams });
	}

	getStudentYears(): Observable<AvailableYearsResponse> {
		return this.http.get<AvailableYearsResponse>(`${this.apiUrl}/me/students/years`);
	}

	getStudentDetail(studentUserId: string): Observable<StudentDetailResponse> {
		return this.http.get<StudentDetailResponse>(`${this.apiUrl}/me/students/${studentUserId}`);
	}

	getStudentApplications(studentUserId: string, page = 1, limit = 20): Observable<PaginatedStudentApplicationsResponse> {
		const httpParams = new HttpParams().set('page', page.toString()).set('limit', limit.toString());
		return this.http.get<PaginatedStudentApplicationsResponse>(
			`${this.apiUrl}/me/students/${studentUserId}/applications`, { params: httpParams }
		);
	}

	addJobApplication(studentUserId: string, payload: AddJobApplicationPayload): Observable<AddJobApplicationResponse> {
		return this.http.post<AddJobApplicationResponse>(`${this.apiUrl}/me/students/${studentUserId}/applications`, payload);
	}

	// ─────────────────────────────────────────────
	// Super admin: college management
	// ─────────────────────────────────────────────

	getAllCollegesList(params: {
		status?: string; search?: string; page?: number; limit?: number;
	}): Observable<PaginatedCollegeListResponse> {
		let query = '';
		const parts: string[] = [];
		if (params.status) parts.push(`status=${encodeURIComponent(params.status)}`);
		if (params.search) parts.push(`search=${encodeURIComponent(params.search)}`);
		parts.push(`page=${params.page ?? 1}`);
		parts.push(`limit=${params.limit ?? 20}`);
		query = `?${parts.join('&')}`;
		return this.http.get<PaginatedCollegeListResponse>(`${this.apiUrl}/admin/list${query}`);
	}

	getCollegeById(collegeId: number): Observable<CollegeProfileResponse> {
		return this.http.get<CollegeProfileResponse>(`${this.apiUrl}/admin/${collegeId}`);
	}

	postCollegeApprovalAction(
		collegeId: number,
		payload: { action: 'approve' | 'reject' | 'block' | 'need_clarification'; notes?: string; rejection_reason?: string; clarification_reason?: string }
	): Observable<CollegeActionResponse> {
		return this.http.post<CollegeActionResponse>(`${this.apiUrl}/admin/${collegeId}/action`, payload);
	}

	updateCollegeDetails(collegeId: number, payload: Record<string, unknown>): Observable<CollegeProfileResponse> {
		return this.http.put<CollegeProfileResponse>(`${this.apiUrl}/admin/${collegeId}`, payload);
	}
}
